import { FunctionComponent, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import PageLayout from "@components/layouts/PageLayout";
import Section from "@components/section/Section";
import BasicButton from "@components/buttons/BasicButton";
import {
  HomeOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import BasicTag from "@components/tags/BasicTag";
import BasicBadge from "@components/badges/BasicBadge";
import BasicDivider from "@components/divider/BasicDivider";
import BasicCard from "@components/cards/BasicCard";
import BasicAlert from "@components/alerts/BasicAlert";
import { useTranslation } from "react-i18next";
import { Form, Tooltip } from "antd";
import InputFormField from "@components/inputs/InputFormField";
import { SelectFormField } from "@components/inputs/SelectFormField";
import { PROFILES } from "@utils/enums/profile.enum";
import BasicModal from "@components/modals/BasicModal";
import DetailItem from "@components/items/DetailItem";
import { formValidateTriggers } from "@utils/Constant";
import SearchFormField from "@components/inputs/SearchFormField";
import { HOME_ROUTE } from "@utils/Routes";
import { toastError, toastSuccess, toastInfo } from "@utils/toast-helper";
import DatePickerFormField from "@components/inputs/DatePickerFormField";
import RangePickerFormField from "@components/inputs/RangePickerFormField";
import CardSection from "@components/section/CardSection";
import Stepper from "@components/stepper/Stepper";
import InfoCard from "@components/cards/InfoCard";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { ReactComponent as Earth } from "src/static/images/earth.svg";
import { ReactComponent as Jobs } from "src/static/images/jobs.svg";
import { ReactComponent as Light } from "src/static/images/light.svg";
import ResultFarmerEnough from "src/static/data/example/result-farmer-enough.json";
import ResultFarmerNotEnough from "src/static/data/example/result-farmer-not-enough.json";
import ResultCollectivityEnough from "src/static/data/example/result-collectivity-enough.json";
import Results from "@components/result/Results";
import { EstimateResultsResponseDto } from "@type/estimateResults/estimateResults.response";
import ResourceOption from "@components/inputs/ResourceOption";
import ResourceFormField from "@components/inputs/ResourceFormField";
import FaqModal from "@components/modals/FaqModal";
import TipsModal from "@components/modals/TipsModal";
import AnswerCard from "@components/cards/AnswerCard";
import ExpertCard from "@components/cards/ExpertCard";
import BasicDrawer from "@components/drawers/BasicDrawer";
import ShareDrawer from "@components/drawers/ShareDrawer";
import SaveDrawer from "@components/drawers/SaveDrawer";

const ComponentsScreen: FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const [isBasicModalVisible, setIsBasicModalVisible] =
    useState<boolean>(false);
  const [isFaqModalVisible, setIsFaqModalVisible] = useState<boolean>(false);
  const [isTipsModalVisible, setIsTipsModalVisible] = useState<boolean>(false);
  const [isBasicDrawerVisible, setIsBasicDrawerVisible] =
    useState<boolean>(false);
  const [isShareDrawerVisible, setIsShareDrawerVisible] =
    useState<boolean>(false);
  const [isSaveDrawerVisible, setIsSaveDrawerVisible] =
    useState<boolean>(false);

  return (
    <PageLayout
      title="Composants - Titre page"
      subtitle="Sous-titre page"
      breadcrumbs={[
        {
          link: HOME_ROUTE,
          icon: <HomeOutlined />,
        },
        {
          label: "Composants",
        },
      ]}
      container
      noContentInCard
    >
      <>
        <Section title="Sections">
          <div>Section content (any content)</div>
        </Section>
        <CardSection title="Card sections">
          <div>Card section content (any content)</div>
        </CardSection>
        <Section title="Boutons">
          <div className="d-flex align-items-center flex-wrap">
            <BasicButton icon={<SearchOutlined />} className="me-3 mb-3" />
            <BasicButton
              icon={<SearchOutlined />}
              text="Primary"
              className="me-3 mb-3"
            />
            <BasicButton text="Primary" className="me-3 mb-3" />
            <BasicButton
              variant="primary-outlined"
              text="Primary outlined"
              className="me-3 mb-3"
            />
            <BasicButton
              variant="secondary"
              text="Secondary"
              className="me-3 mb-3"
            />
            <BasicButton
              variant="tertiary"
              text="Tertiary"
              className="me-3 mb-3"
            />
            <BasicButton
              variant="success"
              text="Success"
              className="me-3 mb-3"
            />
            <BasicButton
              variant="warning"
              text="Warning"
              className="me-3 mb-3"
            />
            <BasicButton variant="danger" text="Danger" className="me-3 mb-3" />
            <BasicButton variant="black" text="Black" className="me-3 mb-3" />
            <BasicButton variant="white" text="White" className="me-3 mb-3" />
            <BasicButton variant="grey" text="Grey" className="me-3 mb-3" />
            <BasicButton
              variant="dark-grey"
              text="Dark grey"
              className="me-3 mb-3"
            />
            <BasicButton
              variant="light-grey"
              text="Light grey"
              className="icon-black me-3 mb-3"
            />
            <BasicButton
              variant="transparent"
              text="Transparent"
              className="mb-3"
            />
          </div>
        </Section>
        <Section title="Badges">
          <div className="d-flex align-items-center flex-wrap">
            <BasicBadge number={1} className="me-3 mb-3" />
            <BasicBadge variant="primary" number={12} className="me-3 mb-3" />
            <BasicBadge variant="secondary" number={24} className="me-3 mb-3" />
            <BasicBadge variant="tertiary" number={36} className="me-3 mb-3" />
            <BasicBadge variant="success" number={48} className="me-3 mb-3" />
            <BasicBadge variant="warning" number={60} className="me-3 mb-3" />
            <BasicBadge variant="danger" number={72} className="me-3 mb-3" />
            <BasicBadge variant="white" number={84} className="me-3 mb-3" />
            <BasicBadge variant="grey" number={96} className="me-3 mb-3" />
            <BasicBadge variant="dark-grey" number={99} className="me-3 mb-3" />
            <BasicBadge variant="light-grey" number={99} className="mb-3" />
          </div>
        </Section>
        <Section title="Tags">
          <div className="d-flex align-items-center flex-wrap">
            <BasicTag label="Default" className="me-3 mb-3" />
            <BasicTag variant="primary" label="Primary" className="me-3 mb-3" />
            <BasicTag
              variant="secondary"
              label="Secondary"
              className="me-3 mb-3"
            />
            <BasicTag
              variant="tertiary"
              label="Tertiary"
              className="me-3 mb-3"
            />
            <BasicTag variant="success" label="Success" className="me-3 mb-3" />
            <BasicTag variant="warning" label="Warning" className="me-3 mb-3" />
            <BasicTag variant="danger" label="Danger" className="me-3 mb-3" />
            <BasicTag variant="white" label="White" className="me-3 mb-3" />
            <BasicTag variant="grey" label="Grey" className="me-3 mb-3" />
            <BasicTag
              variant="dark-grey"
              label="Dark grey"
              className="me-3 mb-3"
            />
            <BasicTag
              variant="light-grey"
              label="Light grey"
              className="mb-3"
            />
          </div>
        </Section>
        <Section title="Dividers">
          <div className="d-flex align-items-center flex-wrap">
            <BasicDivider className="me-3" />
            <BasicDivider variant="primary" className="me-3 mb-3" />
            <BasicDivider variant="secondary" className="me-3 mb-3" />
            <BasicDivider variant="tertiary" className="me-3 mb-3" />
            <BasicDivider variant="success" className="me-3 mb-3" />
            <BasicDivider variant="warning" className="me-3 mb-3" />
            <BasicDivider variant="danger" className="me-3 mb-3" />
            <BasicDivider variant="white" className="me-3 mb-3" />
            <BasicDivider variant="grey" className="me-3 mb-3" />
            <BasicDivider variant="dark-grey" className="me-3 mb-3" />
            <BasicDivider variant="light-grey" className="mb-3" />
          </div>
        </Section>
        <Section title="Cards">
          <>
            <BasicCard className="mb-3">
              <>Basic card</>
            </BasicCard>
            <BasicCard onClick={() => console.log("clickable")}>
              <>Basic clickable card</>
            </BasicCard>
            <InfoCard onClick={() => console.log("clickable")}>
              <>
                <p className="span-title">Pouvoir méthanogène très élevé</p>
                <p className="hero-text mt-4">-1903</p>
                <h5 className="text-primary mt-2 mb-0">t/an de C02</h5>
              </>
            </InfoCard>
          </>
        </Section>
        <Section title="Alerts">
          <>
            <BasicAlert text="Default alert" className="mb-3" />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Default alert with icon"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Primary alert"
              variant="primary"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Primary lighten alert"
              variant="primary-lighten"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Secondary alert"
              variant="secondary"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Secondary lighten alert"
              variant="secondary-lighten"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Tertiary alert"
              variant="tertiary"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Success alert"
              variant="success"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Warning alert"
              variant="warning"
              className="mb-3"
            />
            <BasicAlert
              icon={<SearchOutlined />}
              text="Danger alert"
              variant="danger"
              className="mb-3"
            />
          </>
        </Section>
        <Section title="Toasts">
          <div className="d-flex align-items-center flex-wrap">
            <BasicButton
              variant="primary"
              text="Info toast"
              onClick={() => toastInfo("Info toast")}
              className="me-3 mb-3"
            />
            <BasicButton
              variant="success"
              text="Success toast"
              onClick={() => toastSuccess("Success toast")}
              className="me-3 mb-3"
            />
            <BasicButton
              variant="danger"
              text="Error toast"
              onClick={() => toastError("Error toast")}
              className="mb-3"
            />
          </div>
        </Section>
        <Section title="Inputs">
          <Form {...formValidateTriggers} layout="vertical">
            <InputFormField
              showLabel
              module="users.edit.form.main"
              field="firstName"
              required
              emptyErrorMessage={t("forms.errors.mandatory")}
            />
            <InputFormField
              showLabel
              module="users.edit.form.main"
              field="lastName"
              required
              emptyErrorMessage={t("forms.errors.mandatory")}
              readOnly
            />
            <SelectFormField
              showLabel
              module="users.edit.form.main"
              field="profile"
              required
              options={PROFILES}
            />
            <SelectFormField
              module="users.edit.form.main"
              field="profile"
              required
              options={PROFILES}
              mode="multiple"
            />
            <SelectFormField
              showLabel
              module="users.edit.form.main"
              field="email"
              required
              readOnly
              options={PROFILES}
            />
            <SearchFormField
              maxLength={100}
              module="users.edit.form.main"
              field="email"
            />
            <DatePickerFormField module="users.edit.form.main" field="date" />
            <RangePickerFormField module="users.edit.form.main" field="range" />
          </Form>
        </Section>
        <Section title="Modals">
          <div className="d-flex align-items-center flex-wrap">
            <BasicButton
              text="Basic modal"
              onClick={() => setIsBasicModalVisible(true)}
              className="me-3 mb-3"
            />
            <BasicModal
              title="Basic Modal"
              visible={isBasicModalVisible}
              onOk={() => setIsBasicModalVisible(false)}
              onCancel={() => setIsBasicModalVisible(false)}
              buttonText="Confirmer"
            >
              <div>Basic modal content</div>
            </BasicModal>
          </div>
        </Section>
        <Section title="Items">
          <>
            <DetailItem
              title="Detail item title"
              description="Detail item description"
              className="mb-3"
            />
          </>
        </Section>
        <Section title="Tooltips">
          <>
            <Tooltip placement="top" title="Tooltip content">
              <span className="text-secondary">
                <InfoCircleOutlined />
              </span>
            </Tooltip>
          </>
        </Section>
        <Section title="Inputs spécifiques">
          <>
            <ResourceOption
              key={"resource-option-1"}
              resource={{
                key: "lisiersPorcins",
                label: "Lisiers porcins",
                type: "number",
                units: ["tetes", "m3"],
                potential: 4,
              }}
              onCheck={() => alert("checked")}
              onUncheck={() => alert("unchecked")}
            />
            <ResourceOption
              key={"resource-option-2"}
              resource={{
                key: "green",
                label: "Tonne de déchets verts collectés par an",
                type: "number",
                infoText: "Quelque chose",
                units: ["t"],
                placeholder: "0",
              }}
              onCheck={() => alert("checked")}
              onUncheck={() => alert("unchecked")}
            />

            <BasicDivider
              height={1}
              className="w-100 mt-4 mb-4"
              variant="grey"
            />

            <Form>
              <ResourceFormField
                resource={{
                  key: "city",
                  label: "Ville disposant d'un marché d'intérêt national",
                  infoText: "Quelque chose",
                  type: "enum",
                  options: [
                    {
                      value: "agen",
                      label: "Agen",
                    },
                    {
                      value: "angers",
                      label: "Angers",
                    },
                    {
                      value: "avignon",
                      label: "Avignon",
                    },
                  ],
                  placeholder: "Choisissez une ville de votre territoire",
                }}
                module={"test"}
              />
              <BasicDivider
                height={1}
                className="w-100 mt-4 mb-4"
                variant="grey"
              />
              <ResourceFormField
                resource={{
                  key: "secondaryCulture",
                  label: "Culture intercalaire",
                  type: "number",
                  units: ["ha", "t/an"],
                }}
                module={"test"}
              />
            </Form>
          </>
        </Section>
        <Section title="Stepper">
          <>
            <Stepper
              current={2}
              steps={[
                { title: "Step 1" },
                { title: "Step 2" },
                { title: "Step 3" },
                { title: "Step 4" },
              ]}
            />
          </>
        </Section>
        <Section title="Carousel" className={"overflow-hidden py-5"}>
          <>
            <Carousel
              className="app-carousel"
              swipeable
              draggable
              showDots={true}
              keyBoardControl
              responsive={{
                desktop: {
                  breakpoint: { max: 3000, min: 1024 },
                  items: 3,
                  partialVisibilityGutter: 40,
                },
                tablet: {
                  breakpoint: { max: 1024, min: 464 },
                  items: 2,
                  partialVisibilityGutter: 30,
                },
                mobile: {
                  breakpoint: { max: 464, min: 0 },
                  items: 1,
                  partialVisibilityGutter: 30,
                },
              }}
              customTransition="all .5"
              transitionDuration={500}
              containerClass="carousel-container"
              removeArrowOnDeviceType={["desktop", "tablet", "mobile"]}
              dotListClass="carousel-dots"
              itemClass="d-flex carousel-card me-3 user-select-none"
            >
              <InfoCard icon={<Earth />}>
                <>
                  <p className="span-title">
                    Réduction des gaz à effet de Serre
                  </p>
                  <p className="hero-text mt-4">-1903</p>
                  <h5 className="text-primary mt-2 mb-0">t/an de C02</h5>
                </>
              </InfoCard>
              <InfoCard icon={<Earth />}>
                <>
                  <p className="span-title">
                    Amélioration de la qualité des sols
                  </p>
                  <p className="hero-text mt-4">-503</p>
                  <h5 className="text-primary mt-2 mb-0">t/an de C02</h5>
                </>
              </InfoCard>
              <InfoCard icon={<Earth />}>
                <>
                  <p className="span-title">Énergie locale & renouvelable</p>
                  <p className="mt-4">
                    <span className="hero-text">380</span>
                    <span className="h5 text-primary ms-2 mt-2 mb-0">
                      foyers chauffés
                    </span>
                  </p>
                  <p className="mt-4">
                    <span className="hero-text">50</span>
                    <span className="h5 text-primary ms-2 mt-2 mb-0">bus</span>
                  </p>
                </>
              </InfoCard>
              <InfoCard icon={<Jobs />}>
                <>
                  <p className="span-title">
                    Création d’emplois locaux & directs
                  </p>
                  <p className="hero-text mt-4">1 à 3</p>
                  <h5 className="text-primary mt-2 mb-0">emplois</h5>
                </>
              </InfoCard>
              <BasicCard className="secondary-carousel-card">
                <>
                  <Light aria-hidden="true" focusable="false" />
                  <p className="text-sm mt-4">
                    Consulter le rapport de l’ADEME
                  </p>
                </>
              </BasicCard>
            </Carousel>
          </>
        </Section>
        <Section title={"Résultats d'estimation"}>
          <>
            <Results
              title="Estimation de votre potentiel de production"
              results={ResultFarmerEnough as EstimateResultsResponseDto}
              type="farmer"
            />
            <br />
            <Results
              title="Estimation de votre potentiel de production"
              results={ResultFarmerNotEnough as EstimateResultsResponseDto}
              type="farmer"
            />
            <br />
            <Results
              title="Estimation de votre potentiel de production"
              results={ResultCollectivityEnough as EstimateResultsResponseDto}
              type="collectivity"
            />
            <br />
            <Results
              title="Estimation de votre potentiel de production"
              type="farmer"
            />
          </>
        </Section>
        <Section title="FAQ & Tips Modal">
          <div>
            <BasicButton
              text="Faq modal"
              onClick={() => setIsFaqModalVisible(true)}
              className="me-3 mb-3"
            />
            <FaqModal
              visible={isFaqModalVisible}
              onCancel={() => setIsFaqModalVisible(false)}
              category="calcul-farmer"
              type="farmer"
            />
            <BasicButton
              text="Tips modal"
              onClick={() => setIsTipsModalVisible(true)}
              className="me-3 mb-3"
            />
            <TipsModal
              visible={isTipsModalVisible}
              onCancel={() => setIsTipsModalVisible(false)}
              title="Comment optimiser votre capacité de production ?"
              onClickExpert={() =>
                window.open(
                  "https://projet-methanisation.grdf.fr/contact",
                  "_blank"
                )
              }
            />
          </div>
        </Section>
        <Section title="Expert card">
          <ExpertCard onClick={() => console.log("Expert")} />
        </Section>
        <Section title="Answer card">
          <div className="p-3 bg-light-grey">
            <AnswerCard />
          </div>
        </Section>
        <Section title="Text bloc">
          <div className="text-sm">
            Avertissement : cette simulation évalue un ordre de grandeur de
            votre capacité de production de gaz vert et des indicateurs associés
            à votre territoire. Cette estimation, donnée à titre indicatif, est
            basée sur des hypothèses de calcul réalistes mais ne saurait se
            substituer à une étude d'expert et ne peut en aucun cas constituer
            un document à caractère contractuel.
          </div>
        </Section>
        <Section title="Drawers">
          <div className="d-flex align-items-center flex-wrap">
            <BasicButton
              text="Basic drawer"
              onClick={() => setIsBasicDrawerVisible(true)}
              className="me-3 mb-3"
            />
            <BasicDrawer
              title="Basic drawer"
              visible={isBasicDrawerVisible}
              onClose={() => setIsBasicDrawerVisible(false)}
              footer={<a href="#">Footer link</a>}
            >
              <div>Basic drawer content</div>
            </BasicDrawer>
            <BasicButton
              text="Share drawer"
              onClick={() => setIsShareDrawerVisible(true)}
              className="me-3 mb-3"
            />
            <ShareDrawer
              source={"FARMER"}
              simulationId={undefined}
              onUpdateSimulationId={() => {
                alert("update simulation id");
              }}
              saveSimulation={() => {
                alert("save simulation");
              }}
              visible={isShareDrawerVisible}
              onClose={() => setIsShareDrawerVisible(false)}
            />
            <BasicButton
              text="Save drawer"
              onClick={() => setIsSaveDrawerVisible(true)}
              className="me-3 mb-3"
            />
            <SaveDrawer
              isCollectivity
              visible={isSaveDrawerVisible}
              onClose={() => setIsSaveDrawerVisible(false)}
              onUpdateSimulationId={() => {
                alert("update simulation id");
              }}
              saveSimulation={() => {
                alert("save simulation");
              }}
            />
          </div>
        </Section>
        <br />
        <br />
        <br />
        <br />
      </>
    </PageLayout>
  );
};

export default ComponentsScreen;
